import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  Spinner,
  Button,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { connect, useDispatch } from 'react-redux';
import classNames from 'classnames';
import hash from 'object-hash';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight, faImageCircleArrowDown } from '@fortawesome/pro-regular-svg-icons';

import EventSelector from '../../../components/EventSelector/Modal';
import EventSelectorButton from '../../../components/EventSelector/Button';
import UpdateButton from '../../Template/UpdateButton';
import RoundButton from '../../../components/RoundButton';
import ColorPicker from '../../../components/ColorPicker';

import { generateScreenshots, generatePreviewScreenshots } from '../../Template/Colors/apis';
import { useUpdateEvent } from '../../Template/hooks';
import { fetchEvent } from '../../Template/api';
import { updateEvent as updateEventInStore } from '../../../store/actions/events';
import {
  fetchConfig,
  getSelfieCam,
  getSelfieCamFromEvent,
} from '../api';
import { updateEventsGeneric } from '../../../helpers/api';
import { displaySuccessModal, displayErrorModal } from '../../../helpers/swal';
import imgPlaceholder from '../../../assets/images/image-placeholder.svg';

import style from '../../Template/Colors/style.module.scss';

const { origin } = window.location;

const DEFAULT_SELFIE_SCREENSHOTS = [
  { page: 'home', path: '?preview=1&eventStatus=live', group: 'Home' },
  { page: 'home-pre', path: '?preview=1&eventStatus=pre', group: 'Home' },
  { page: 'home-post', path: '?preview=1&eventStatus=post', group: 'Home' },
  { page: 'terms', path: 'terms/?preview=1&eventStatus=live', group: 'Onboarding' },
  { page: 'onboarding', path: 'onboarding/?preview=1&eventStatus=live', group: 'Onboarding' },
  { page: 'userinfo', path: 'userinfo/?preview=1&eventStatus=live', group: 'Onboarding' },
  { page: 'players', path: 'players/?preview=1&eventStatus=live', group: 'Onboarding' },
  { page: 'camera', path: 'camera/?preview=1&eventStatus=live', group: 'Camera' },
  { page: 'camera-captured', path: 'camera/?preview=1&eventStatus=live&captured=1', group: 'Camera' },
  { page: 'album', path: 'album/?preview=1&eventStatus=live', group: 'Album' },
  { page: 'album-captured', path: 'album/?preview=1&eventStatus=live&captured=1', group: 'Album' },
  { page: 'contactus', path: 'contactus/?preview=1&eventStatus=live', group: 'Other' },
  { page: 'language', path: 'language/?preview=1&eventStatus=live', group: 'Other' },
  { page: '404', path: '404/?preview=1&eventStatus=live', group: 'Other' },
  { page: '401', path: '401/?preview=1&eventStatus=live', group: 'Other' },
];

const getColorsFromEvent = (evt) => {
  const sc = getSelfieCamFromEvent(evt);
  return sc?.colors && typeof sc.colors === 'object' ? sc.colors : null;
};

const AiTemplateColors = ({ event, template, allEvents }) => {
  const dispatch = useDispatch();
  const isEventUpdating = useUpdateEvent();
  const screenshots = (event?.screenshots
    && Array.isArray(event.screenshots)
    && event.screenshots.length > 0)
    ? event.screenshots
    : DEFAULT_SELFIE_SCREENSHOTS;

  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [theme, setTheme] = useState({});
  const colorKeys = Object.keys(theme);

  const [selectedPage, setSelectedPage] = useState(screenshots[0]);
  const [isExporting, setIsExporting] = useState(false);
  const [hashStr, setHashStr] = useState();
  const [pageHashes, setPageHashes] = useState({});
  const [pagePaths, setPagePaths] = useState({});
  const [pageLoadingStates, setPageLoadingStates] = useState({});
  const [pageTimeoutStates, setPageTimeoutStates] = useState({});
  const previewRef = useRef(false);

  const eventColorsKey = JSON.stringify(getColorsFromEvent(event) || null);

  useEffect(() => {
    const eventColors = getColorsFromEvent(event);

    if (eventColors && Object.keys(eventColors).length > 0) {
      setTheme({ ...eventColors });
      setConfigLoading(false);
      fetchConfig()
        .then((data) => setConfig(data))
        .catch(() => {});
      return () => {};
    }

    setTheme({});
    setConfigLoading(true);

    let cancelled = false;
    fetchConfig()
      .then((data) => {
        if (cancelled) return;
        setConfig(data);
        const selfieCam = getSelfieCam(data);
        const configColors = selfieCam?.colors && typeof selfieCam.colors === 'object'
          ? selfieCam.colors
          : {};
        setTheme({ ...configColors });
      })
      .catch(() => {
        if (!cancelled) displayErrorModal({ text: 'Failed to load config.' });
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });

    return () => { cancelled = true; };
  }, [event?.slugDate, event?._id, eventColorsKey]);

  useEffect(() => {
    if (event && screenshots.length) {
      setHashStr(event.screenshotsHash || null);
      const initialHashes = {};
      const initialPaths = {};
      screenshots.forEach(({ page }) => {
        initialHashes[page] = event.screenshotsHash;
        initialPaths[page] = 'saved';
      });
      setPageHashes(initialHashes);
      setPagePaths(initialPaths);
      setSelectedPage((prev) => screenshots.find((s) => s.page === prev?.page) || screenshots[0]);
    }
  }, [event, event?.screenshotsHash, screenshots]);

  const updateColor = useCallback((hex, colorName) => {
    setTheme((prev) => ({ ...prev, [colorName]: hex }));
  }, []);

  const getImage = useCallback(({ page }, alt) => {
    if (!hashStr) {
      return (
        <div className={style.imgPlaceholder}>
          <img src={imgPlaceholder} alt={alt} />
        </div>
      );
    }
    const pageHash = pageHashes[page] || hashStr;
    const pagePath = pagePaths[page] || (previewRef.current ? 'preview' : 'saved');
    const url = `${origin}/screenshots/${pagePath}/${pageHash}/${page}.jpeg`;
    return <img src={url} alt={alt} />;
  }, [hashStr, pageHashes, pagePaths]);

  const handleExportColors = () => {
    const blob = new Blob([JSON.stringify({ colors: { theme } }, null, 2)], { type: 'application/json' });
    saveAs(blob, event?.slugDate ? `${event.slugDate}-colors.json` : 'selfie-cam-colors.json');
  };

  const isValidHex = (color) => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);

  const handleImportColors = (evt) => {
    const file = evt.target.files?.[0];
    const isJsonFile = file?.type === 'application/json';
    if (!isJsonFile) {
      if (file) displayErrorModal({ text: 'Invalid file type. Please upload a JSON file.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        const themeData = jsonData.selfieCam?.colors
          || jsonData.selfiecam?.colors
          || jsonData.colors?.theme
          || jsonData.colors
          || jsonData.theme
          || {};
        const validTheme = Object.fromEntries(
          Object.entries(themeData).filter(([, color]) => isValidHex(color)),
        );
        if (Object.keys(validTheme).length > 0) {
          setTheme((prev) => ({ ...prev, ...validTheme }));
          displaySuccessModal({ text: 'Imported colors successfully.' });
        } else {
          displayErrorModal({ text: 'No valid theme colors found in the file.' });
        }
      } catch {
        displayErrorModal({ text: 'Invalid JSON or format.' });
      }
      // eslint-disable-next-line no-param-reassign
      evt.target.value = '';
    };
    reader.readAsText(file);
  };

  const handleRefreshScreenshot = useCallback(async (pageName) => {
    if (!event || pageLoadingStates[pageName] || pageTimeoutStates[pageName]) return;
    setPageLoadingStates((prev) => ({ ...prev, [pageName]: true }));
    setPageTimeoutStates((prev) => ({ ...prev, [pageName]: true }));
    setTimeout(() => setPageTimeoutStates((prev) => ({ ...prev, [pageName]: false })), 3000);
    try {
      const refreshHash = hash({
        theme, slugDate: event.slugDate, page: pageName, timestamp: Date.now(),
      });
      const screenshotsToGenerate = screenshots.filter((s) => s.page === pageName);
      await generatePreviewScreenshots({
        screenshots: screenshotsToGenerate,
        hash: refreshHash,
        slugDate: event.slugDate,
        theme,
        iconColor: {},
        emailSettings: {},
      });
      previewRef.current = true;
      setPageHashes((prev) => ({ ...prev, [pageName]: refreshHash }));
      setPagePaths((prev) => ({ ...prev, [pageName]: 'preview' }));
    } catch (err) {
      console.error('Error refreshing screenshot', err);
    } finally {
      setPageLoadingStates((prev) => ({ ...prev, [pageName]: false }));
    }
  }, [event, theme, screenshots, pageLoadingStates, pageTimeoutStates]);

  const requestScreenshots = useCallback(async ({ preview = true, eventIds }) => {
    if (!event) return;
    const loadingState = {};
    screenshots.forEach(({ page }) => { loadingState[page] = true; });
    setPageLoadingStates(loadingState);
    try {
      const newHash = hash({ theme, slugDate: event.slugDate });
      if (preview) {
        await generatePreviewScreenshots({
          screenshots,
          hash: newHash,
          slugDate: event.slugDate,
          theme,
          iconColor: {},
          emailSettings: {},
        });
        previewRef.current = true;
        setHashStr(newHash);
        const nextHashes = {}; const nextPaths = {};
        screenshots.forEach(({ page }) => { nextHashes[page] = newHash; nextPaths[page] = 'preview'; });
        setPageHashes(nextHashes); setPagePaths(nextPaths);
      } else {
        previewRef.current = false;
        await generateScreenshots({ hash: newHash, screenshots, eventIds });
        setHashStr(newHash);
        const nextHashes = {}; const nextPaths = {};
        screenshots.forEach(({ page }) => { nextHashes[page] = newHash; nextPaths[page] = 'saved'; });
        setPageHashes(nextHashes); setPagePaths(nextPaths);
      }
    } catch (err) {
      console.error('Error generating screenshots', err);
    } finally {
      setPageLoadingStates({});
    }
  }, [event, theme, screenshots]);

  const updateEventHandler = useCallback(async (eventIds) => {
    if (!config || !eventIds?.length) return;
    const slugDates = eventIds
      .map((id) => (template?._id === id ? template : allEvents.find((e) => e._id === id)))
      .filter(Boolean)
      .map((e) => e.slugDate)
      .filter(Boolean);
    if (!slugDates.length) throw new Error('No events selected to update.');

    await updateEventsGeneric(eventIds, { 'selfiecam.colors': theme });

    setConfig((prev) => (prev
      ? {
        ...prev,
        selfieCam: prev.selfieCam ? { ...prev.selfieCam, colors: theme } : { colors: theme },
      }
      : null));

    await Promise.all(slugDates.map(async (sd) => {
      try {
        const fresh = await fetchEvent(sd);
        dispatch(updateEventInStore(fresh));
      } catch (err) { console.error(err); }
    }));

    await requestScreenshots({ preview: false, eventIds });
  }, [theme, config, template, allEvents, requestScreenshots, dispatch]);

  const handleExportScreenshots = useCallback(async () => {
    if (!event || !hashStr) {
      displayErrorModal({ text: 'No screenshots available to export.' });
      return;
    }
    setIsExporting(true);
    try {
      const zip = new JSZip();
      const results = await Promise.all(screenshots.map(async (screenshot) => {
        try {
          const pageHash = pageHashes[screenshot.page] || hashStr;
          const pagePath = pagePaths[screenshot.page] || (previewRef.current ? 'preview' : 'saved');
          const url = `${origin}/screenshots/${pagePath}/${pageHash}/${screenshot.page}.jpeg`;
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Failed to fetch ${screenshot.page}`);
          const blob = await response.blob();
          return { name: `${screenshot.page}.jpeg`, data: blob };
        } catch (e) {
          console.error(e);
          return null;
        }
      }));
      const valid = results.filter(Boolean);
      if (valid.length === 0) {
        displayErrorModal({ text: 'No screenshots could be downloaded.' });
        return;
      }
      valid.forEach(({ name, data }) => zip.file(name, data));
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const timestamp = new Date().toISOString().replaceAll(/[:.]/g, '-').slice(0, -5);
      saveAs(zipBlob, `${event.slugDate}-screenshots-${timestamp}.zip`);
      displaySuccessModal({ text: `Successfully exported ${valid.length} screenshots.` });
    } catch (error) {
      console.error('Error exporting screenshots', error);
      displayErrorModal({ text: 'Failed to export screenshots.' });
    } finally {
      setIsExporting(false);
    }
  }, [event, hashStr, screenshots, pageHashes, pagePaths]);

  const isPageLoading = (pageName) => pageLoadingStates[pageName] || false;
  const isPageDisabled = (pageName) => (pageLoadingStates[pageName]
    || pageTimeoutStates[pageName]
    || false);

  if (configLoading || (isEventUpdating && !event)) {
    return (
      <div className="flex-grow-1 d-flex justify-content-center align-items-center">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column flex-grow-1">
      <div className="pageHeader">
        <h4>UI Images</h4>
        <EventSelectorButton showFutureEvents />
        <div className="d-flex flex-row">
          <Button variant="outline-primary" size="sm" className={classNames('mr-2 rounded-pill', style.importBtn)}>
            Import Colors
            {' '}
            <input type="file" accept=".json,application/json" onChange={handleImportColors} />
          </Button>
          <Button variant="outline-primary" size="sm" onClick={handleExportColors} className="mr-2 rounded-pill">
            Export Colors
          </Button>
          <UpdateButton serverErrorMessage label="Update Event" updateHandler={updateEventHandler} />
        </div>
      </div>

      {isEventUpdating && (
        <div className="flex-grow-1 d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      {!isEventUpdating && selectedPage && screenshots.length > 0 && (
        <div className={style.gridContainer}>
          <div className={classNames('d-flex', 'flex-column', style.formArea)}>
            <div className="d-flex">
              <div className="flex-grow-1">
                <p className="mb-0">
                  Edit theme colours from config.
                </p>
              </div>
              <div>
                <Button
                  className="small text-nowrap"
                  variant="outline-primary"
                  onClick={() => requestScreenshots({ preview: true })}
                >
                  Preview all
                </Button>
              </div>
            </div>
            {colorKeys.length === 0 ? (
              <p className="text-muted small">No colors in config.selfieCam yet. Add a colors object to the config to edit here.</p>
            ) : (
              <div className="mt-2">
                {colorKeys.map((key) => (
                  <ColorPicker
                    key={key}
                    title={key.replace(/([A-Z])/g, ' $1').trim()}
                    colorName={key}
                    value={theme[key] || '#ffffff'}
                    onChange={updateColor}
                  />
                ))}
              </div>
            )}
          </div>
          <div className={style.previewArea}>
            <div>
              <div className="d-flex justify-content-between align-items-center mt-3 mb-1 ml-3 mr-3">
                <div className="flex-grow-1 text-left">
                  <h5 className="ml-2">{selectedPage.page}</h5>
                </div>
                <OverlayTrigger placement="top" overlay={<Tooltip>Refresh current screenshot only</Tooltip>}>
                  <span className="mr-2">
                    <RoundButton
                      onClick={() => handleRefreshScreenshot(selectedPage.page)}
                      disabled={isPageDisabled(selectedPage.page)}
                      className={classNames(style.refreshBtn, { [style.refreshBtnDisabled]: isPageDisabled(selectedPage.page) })} // eslint-disable-line max-len
                    >
                      <FontAwesomeIcon icon={faRotateRight} className={isPageLoading(selectedPage.page) ? style.spinning : ''} />
                    </RoundButton>
                  </span>
                </OverlayTrigger>
                <OverlayTrigger placement="top" overlay={<Tooltip>Export Screenshots</Tooltip>}>
                  <span className="mr-2">
                    <RoundButton
                      onClick={handleExportScreenshots}
                      disabled={isExporting || !hashStr}
                      className={classNames(style.refreshBtn, { [style.refreshBtnDisabled]: isExporting || !hashStr })} // eslint-disable-line max-len
                    >
                      <FontAwesomeIcon icon={faImageCircleArrowDown} />
                    </RoundButton>
                  </span>
                </OverlayTrigger>
              </div>
              <div className={style.previewContainer}>
                <div>
                  {isPageLoading(selectedPage.page) ? (
                    <div className="d-flex align-items-center">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    getImage(selectedPage, 'main preview')
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={style.carouselArea}>
            <div className={style.PagesCarousel}>
              {screenshots.map((item, index) => (
                <div
                  key={item.page}
                  role="button"
                  tabIndex={0}
                  className={style.carouselItem}
                  onClick={() => setSelectedPage(item)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelectedPage(item)}
                >
                  <h6>
                    {index === 0 || screenshots[index - 1].group !== item.group ? item.group : ' '}
                  </h6>
                  <div
                    className={classNames(style.imageContainer, { [style.active]: item === selectedPage })} // eslint-disable-line max-len
                  >
                    {isPageLoading(item.page) ? (
                      <Spinner animation="border" variant="primary" />
                    ) : (
                      getImage(item, item.page)
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <EventSelector singleSelection showTemplate showFutureEvents />
    </div>
  );
};

export default connect(({ events: { selectedEvents, template, allEvents } }) => ({
  event: selectedEvents[0] || template,
  template,
  allEvents: allEvents || [],
}))(AiTemplateColors);
